import { StarFilled } from '@ant-design/icons';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import React from 'react';
import appStore from '../../assets/app-store-badge.svg';
import playStore from '../../assets/google-play-badge.svg';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { OmniaLinkButton } from '../../components/omnia_button/omnia_link_button';
import { ProPurchaseButton } from '../../components/pro_buttons/pro_purchase_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { Spacer } from '../../components/spacer/spacer';
import { useResponsive } from '../../hooks/use_responsive';
import { RoutePaths } from '../../routes';
import tenback from './assets/10back.webp';
import anglerDesktop from './assets/angler-desktop.webp';
import anglerMobile from './assets/angler-mobile.webp';
import appFeatures1 from './assets/appfeatures-1.webp';
import appFeatures2 from './assets/appfeatures-2.webp';
import appFeatures3 from './assets/appfeatures-3.webp';
import appFeatures4 from './assets/appfeatures-4.webp';
import appFeatures5 from './assets/appfeatures-5.webp';
import bottomhardness from './assets/bottomhardness.webp';
import clarity from './assets/clarity.webp';
import contours from './assets/contours.webp';
import patrick from './assets/patrickwalters.webp';
import proLogo from './assets/pro-logo.svg';
import top1 from './assets/top-1.webp';
import top2 from './assets/top-2.webp';
import top3 from './assets/top-3.webp';
import top4 from './assets/top-4.webp';
import unlock from './assets/unlock.webp';
import vegetation from './assets/vegetation.webp';
import waterTemp from './assets/watertemp.webp';
import weather from './assets/weather.webp';
import wind from './assets/wind.webp';

const appImgs = [top1, top2, top3, top4];

const appFeatures = [
  {
    img: appFeatures1,
    title: 'Map-Based Shopping, Built for Anglers.',
    description:
      'Tap the lake pin, see trending tackle and techniques, and have baits delivered to you quickly. We narrow over 25k items to the best match on each lake.',
  },
  {
    img: appFeatures2,
    title: 'Free Local Fishing Reports',
    description:
      'Real reports from real anglers. Tap a lake, get the latest on what’s biting, where, and why. Pull from over 30,000+ verified fishing reports—and counting.',
  },
  {
    img: appFeatures3,
    title: 'Scout Smarter with Real-Time Lake Intel',
    description:
      'Then layer in lake-wide water temperature, barometric pressure, and moon phase to understand when fish are most likely to feed—and where they’ll show up.',
  },
  {
    img: appFeatures4,
    title: 'Waypoint Management: Create, Import and Pattern Like a Pro.',
    description:
      'Drop pins. Import old ones. Then filter by wind, pressure, moon phase, or season to build out what’s working—and when. Syncs across mobile, desktop, and tablet.',
  },
  {
    img: appFeatures5,
    title: 'Find Structure with AI-Fueled Fishing Reports Summaries',
    description:
      'Season, depth, species, and more—crunched into a simple plan so you don’t waste time guessing.',
  },
];

const DownloadButtons = (
  <div className="flex gap-3 justify-center">
    <a href="https://go.omniafishing.com/gZRj/wepq4apf">
      <img src={appStore} alt="Download on the App Store" width={177} />
    </a>
    <a href="https://go.omniafishing.com/gZRj/wepq4apf">
      <img src={playStore} alt="Get it on Google Play" width={200} />
    </a>
  </div>
);

const BulletPoints = (
  <>
    <div className="flex flex-col items-end sm:items-start pr-3 sm:pr-0 sm:flex-row gap-2 sm:gap-5 font-bold justify-center font-termina mb-6 text-xs sm:text-[13px]">
      <span>150k+ Lakes Covered</span>
      <span>30k+ Fishing Reports</span>
      <span>11 Map Layers</span>
    </div>
  </>
);

const layers = [
  {
    img: wind,
    title: 'Wind',
    description:
      'Get current wind speed and direction. Wind affects water movement and can push baitfish into certain areas, attracting larger fish. Knowing the wind patterns helps you position yourself in the best spots.',
  },
  {
    img: contours,
    title: 'Compare Contours',
    description:
      'Toggle between premium Navionics® and C-MAP® charts. Access depth contours to pinpoint drop-offs, ledges, and deep holes where fish might be lurking. ',
  },
  {
    img: waterTemp,
    title: 'Water Temperature',
    description:
      'Get water temperature data to find where fish are most active. Target the best spots by tracking temperature variations.',
  },
  {
    img: clarity,
    title: 'Water Clarity',
    description:
      'Get water clarity data to choose the right lure and technique. Clear water often means fish can see further, while murky water might require more visible or noisy lures.',
  },
  {
    img: bottomhardness,
    title: 'Bottom Composition',
    description:
      'Get bottom composition data to find where different fish types lurk. Use this insight to target the right spots and fish more effectively.',
  },
  {
    img: vegetation,
    title: 'Vegetation',
    description:
      'Access vegetation data to spot weed beds and other cover. Target these areas to boost your chances of a successful catch.',
  },
  {
    img: weather,
    title: 'Weather',
    description:
      'Get critical weather info on cloud cover, barometric pressure, live storms, temperature, and rain updates. Stay safe with radar, storm cell, and lightning strike data, keeping you informed of changing fishing conditions.',
  },
];

const reviews = [
  {
    user: 'Larry Koch',
    quote:
      'Best service for fishing tackle. Fast and free shipping every time. I love the maps for planning my next trip and checking recent angler reports.',
  },
  {
    user: 'Joseph Doherty',
    quote:
      'Between the detailed maps and the ability to see exactly what to look for in a lake, Omnia is all you need for fishing success. Super‑fast shipping seals the deal.',
  },
  {
    user: 'Rob “Arksis” Rivera',
    quote:
      'Everything a fisherman needs in one app! I use the Pro maps every trip to find the right spots and get free shipping with my membership.',
  },
  {
    user: 'Seanybean',
    quote:
      'Quality, accurate maps for every body of water I’ve fished. Love being able to mark the spots where I’ve actually caught fish.',
  },
  {
    user: 'LoganAndera',
    quote:
      'Love the Omnia app. The map overlays—especially Navionics and real‑time water temp—are game‑changers, and free fast shipping on tackle is huge.',
  },
  {
    user: 'pointfishing',
    quote:
      'The Omnia app lets me shop and see map layers in one place. Premium membership adds water temp, wind speed, clarity, and more—super helpful for planning.',
  },
  {
    user: 'bugscuffle',
    quote:
      'Originally downloaded for a promo, but I’m renewing because the charts are incredibly detailed and have helped me catch way more fish.',
  },
  {
    user: 'DollaBillGill',
    quote:
      'I fish a lot, so the points that give me free products and the unlimited fast shipping—plus Pro‑only deals—are an unbeatable combo.',
  },
  {
    user: 'Nullthreat',
    quote:
      'I’ve used Omnia Fishing Maps & Tackle for several years—it’s grown into much more than a tackle‑shopping app; it’s essential for every trip.',
  },
  {
    user: 'JJacobs88',
    quote:
      'Always super‑fast shipping. Premium membership means free unlimited shipping plus 10% back in credits for future purchases—no brainer.',
  },
];

const AppPage = () => {
  const { isMobile, isDesktop } = useResponsive();
  const appFeaturesList = isMobile ? appFeatures : appFeatures.slice(1);

  const [layersEmblaRef] = useEmblaCarousel(
    {
      align: 'start',
      dragFree: true,
    },
    []
  );
  const [reviewsEmblaRef] = useEmblaCarousel(
    {
      align: 'start',
      dragFree: true,
    },
    []
  );

  return (
    <ScrollToTop>
      <div className="bg-[#E9EEEC] pt-12">
        <ContentWrapper>
          <section className="text-center">
            <p className="font-bold text-[#FF2A00] font-termina text-[13px] mb-2">
              Omnia Fishing App
            </p>
            <h1 className="text-xl sm:text-[32px] leading-tight font-bold mb-6 px-3">
              Find Fish-Rich Water. Trending Tackle.{' '}
              <span className="sm:block">And Free Local Fishing Reports.</span>
            </h1>

            {isDesktop && BulletPoints}

            <div className="flex justify-center gap-2 mb-6 items-center text-[#FF2A00]">
              <span className="text-xs">4.7</span>
              <div className="flex items-center gap-1">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="w-[18px] h-[18px] relative">
                    <StarFilled className="text-lg absolute left-0 w-[18px]" />
                  </div>
                ))}
                <div className="w-[12px] h-[18px] relative overflow-hidden">
                  <StarFilled className="text-lg absolute left-0 w-[18px]" />
                </div>
              </div>
              <span className="text-xs">(1100+)</span>
            </div>

            {DownloadButtons}

            <Spacer size={20} />
            <p className="font-bold text-[#FF2A00] font-termina font-xs">Download Free</p>
            <Spacer size={20} />

            <div className="overflow-hidden -mx-3 sm:m-0 mb-6 sm:mb-0">
              <div className="flex gap-3 justify-center w-[190%] -ml-[20%] sm:w-auto sm:gap-6 sm:ml-0">
                {appImgs.map((img, index) => (
                  <div key={index} className="">
                    <img src={img} alt={`Omnia Fishing App Screenshot ${index + 1}`} className="" />
                  </div>
                ))}
              </div>
            </div>

            {isMobile && (
              <>
                {BulletPoints}
                <Spacer size={20} />
                <div className="-mr-3 w-[90%] ml-auto">
                  <img src={anglerMobile} alt="" />
                </div>
                <Spacer size={20} />
              </>
            )}

            <q
              className={classNames(
                'py-8 flex flex-col max-w-[600px] mx-auto px-6 font-termina text-xl text-[#061622]',
                'after:content-["“"] before:content-["”"] before:text-[#FF2A00] after:text-[#FF2A00] before:text-[40px] after:text-[40px] before:font-termina after:font-termina before:font-bold after:font-bold after:translate-y-2.5'
              )}
            >
              I love the ease of use and all the different features from contour lines to water
              clarity.
              <br />- Jason S.
            </q>
          </section>

          <section className="relative pb-[170px]">
            <div
              className={classNames('absolute -left-3 -right-3 h-[650px] ', {
                'bg-[#061622] top-10 rounded-[90px]': isMobile,
                'pt-10': isDesktop,
              })}
            >
              {isDesktop && <img src={anglerDesktop} />}
            </div>
            <div className="relative z-10 flex flex-col items-center">
              <div className="max-w-[250px] sm:max-w-[800px] grid grid-cols-1 sm:grid-cols-2 gap-y-12 gap-x-6">
                {isDesktop && (
                  <>
                    <div className="text-white flex flex-col justify-center gap-3 pr-10">
                      <h2 className="text-[36px] leading-[1.05] font-bold font-termina mb-2">
                        {appFeatures[0].title}
                      </h2>
                      <p className="text-base leading-normal">{appFeatures[0].description}</p>
                    </div>
                    <div className="">
                      <img src={appFeatures[0].img} alt="" />
                    </div>
                  </>
                )}

                {appFeaturesList.map((feature, index) => {
                  const textWhite = isMobile && index === 0;
                  return (
                    <div
                      key={index}
                      className={classNames('flex flex-col gap-3', {
                        'text-white': textWhite,
                        'text-[#061622]': !textWhite,
                      })}
                    >
                      <img src={feature.img} alt={feature.title} className="mb-3" />
                      <h2 className="text-lg font-bold font-termina">{feature.title}</h2>
                      <p className="text-base leading-normal">{feature.description}</p>
                    </div>
                  );
                })}
              </div>

              <Spacer size={32} />
              {DownloadButtons}

              <Spacer size={32} />

              <p className="font-bold text-[#FF2A00] font-termina font-xs text-center px-10">
                Wanna get really smart about your lakes with the right plan?
              </p>
            </div>
            <div className="absolute -left-3 -right-3 bottom-0 h-[650px] rounded-[90px] rounded-bl-none rounded-br-none bg-[#D0DADE]" />
          </section>

          <section className="relative -mt-[90px] pb-14 sm:pb-20">
            <div className="absolute -left-3 -right-3 top-0 h-[830px] rounded-[90px] rounded-bl-none rounded-br-none bg-[#061622] " />

            <div className="flex flex-col-reverse sm:justify-center sm:flex-row items-center text-white relative w-[280px] sm:w-auto mx-auto gap-6">
              <div className="sm:w-[320px] flex flex-col gap-6 items-start">
                <img src={proLogo} alt="Omnia PRO" />
                <h2 className="text-2xl font-bold">Unlock Deeper Map Layers and Earn Rewards</h2>
                <OmniaLinkButton to={RoutePaths.PRO} kind="primary" size="lg">
                  Explore
                </OmniaLinkButton>
              </div>
              <div className="-mt-6 sm:-mt-10">
                <img src={unlock} width={500} alt="" />
              </div>
            </div>

            <div className="relative">
              <div className="py-10 w-[280px] sm:w-auto mx-auto">
                <ul className="text-base font-termina text-white font-bold sm:flex sm:justify-center gap-1">
                  <li>
                    11 Map Layers <span className="text-red-500">+</span>
                  </li>
                  <li>
                    {' '}
                    10% Back on Gear <span className="text-red-500">+</span>
                  </li>
                  <li>Ships from MN</li>
                </ul>
              </div>

              <div ref={layersEmblaRef} className="overflow-hidden -ml-3 -mr-3">
                <div className="flex gap-6 pl-12 w-full select-none">
                  {layers.map((layer) => {
                    return (
                      <div
                        key={layer.title}
                        className="flex flex-col gap-2 w-[55%] sm:w-[335px] flex-grow-0 flex-shrink-0"
                      >
                        <img src={layer.img} alt={layer.title} />
                        <h3 className="text-lg font-bold">{layer.title}</h3>
                        <p className="">{layer.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-[#061622] rounded-[90px] p-[60px] sm:py-[70px] -mx-3 flex justify-center">
            <div className="flex flex-col-reverse sm:flex-row items-center gap-6">
              <div className="text-white sm:w-[300px]">
                <p className="font-termina font-bold text-lg sm:text-[36px] leading-[1.1] mb-6">
                  <span className="text-[#FF2A00] sm:block">Plus. </span>
                  10% Back On Orders
                </p>
                <p className="text-base leading-relaxed mb-10 sm:mb-14">
                  Shop gear built for your lake.
                  <br />
                  Orders before 1PM CT ship same day from our MN warehouse.
                </p>

                <ProPurchaseButton size="lg" kind="primary" text="Sign Up" />
              </div>
              <div>
                <img src={tenback} alt="" width={335} />
              </div>
            </div>
          </section>

          <section className="bg-[#D0DADE] rounded-[90px] p-[60px] sm:py-[70px] -mx-3">
            <p className="text-xs font-bold uppercase text-[#FF2A00] mb-3 font-termina">Reviews</p>
            <h3 className="font-termina font-bold text-2xl sm:text-[40px] text-[#061622] mb-3 sm:mb-6">
              Tough Tests, Real Results
            </h3>

            <div ref={reviewsEmblaRef} className="overflow-hidden -ml-[60px] -mr-[60px] mb-12">
              <div className="flex gap-6 pl-12 w-full select-none">
                {reviews.map((review, index) => {
                  return (
                    <div
                      key={index}
                      className="flex flex-col gap-2 w-[290px] flex-grow-0 flex-shrink-0 bg-[#E9EEEC] rounded-[40px] p-6"
                    >
                      <div className="text-[#FF2A00]">
                        <StarFilled />
                        <StarFilled />
                        <StarFilled />
                        <StarFilled />
                        <StarFilled />
                      </div>
                      <q>{review.quote}</q>
                      <p className="font-termina text-base mt-auto">{review.user}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="font-termina text-2xl sm:text-[32px] text-[#061622] mb-6">
              And Field Tested by PROs
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="bg-[#061622] text-white p-9 pt-24 sm:p-20 rounded-[40px] flex items-center">
                <q className="flex flex-col font-termina font-light text-xl sm:text-[32px] leading-[1.3] text-[#D0DADE] after:content-[''] before:content-['“'] before:text-[#FF2A00] before:text-[190px] before:font-termina before:leading-[0.2]">
                  Water temperature is a key data point for making a decision on predicting fish
                  patterns, helping me choose techniques and informing my strategy for catching big
                  bass. These Omnia maps are a game changer!”
                </q>
              </div>
              <div className="flex flex-col gap-6">
                <div>
                  <img src={patrick} alt="" />
                </div>
                <div className="bg-[#E9EEEC] p-12 rounded-[40px]">
                  <p className="font-termina text-4xl">Patrick Walters</p>
                  <p className="text-[#6B7172] text-base font-light">Bassmaster Elite Series Pro</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-[#FF2A00] rounded-[90px] rounded-bl-none rounded-br-none p-[60px] sm:py-[70px] -mx-3">
            <p className="font-termina font-bold text-white text-xl text-center mb-14">
              Download the Omnia Fishing App on your phone
            </p>
            {DownloadButtons}
          </section>
        </ContentWrapper>
      </div>
    </ScrollToTop>
  );
};

export default AppPage;
